"use client";

import { useEffect, useMemo, useState } from "react";
import { Chess } from "chess.js";
import { boardSvg } from "@/lib/chess/diagram";
import { BOARDS, type BoardFinish } from "@/lib/appearance";

const START = "rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1";
const SIZES = [400, 800, 1600] as const;

/**
 * The board part of a FEN, or the final position of a PGN. Diagrams often show partial
 * positions (no kings, impossible material), so only the layout itself is checked.
 */
export function readPosition(input: string): { placement: string; error?: undefined } | { placement?: undefined; error: string } {
  const text = input.trim();
  if (!text) return { error: "Paste a FEN or a PGN." };
  if (/^\[|^\d+\.\s*\S/.test(text)) {
    try {
      const game = new Chess();
      game.loadPgn(text);
      return { placement: game.fen().split(" ")[0] };
    } catch {
      return { error: "That looks like a PGN, but it does not replay. Check the moves." };
    }
  }
  const placement = text.split(/\s+/)[0];
  const ranks = placement.split("/");
  if (ranks.length !== 8) return { error: `A FEN board has 8 ranks separated by "/"; this one has ${ranks.length}.` };
  for (const [i, rank] of ranks.entries()) {
    if (!/^[prnbqkPRNBQK1-8]+$/.test(rank)) return { error: `Rank ${8 - i} contains a character that is not a piece letter or a number.` };
    const squares = [...rank].reduce((n, ch) => n + (/\d/.test(ch) ? Number(ch) : 1), 0);
    if (squares !== 8) return { error: `Rank ${8 - i} covers ${squares} squares instead of 8.` };
  }
  return { placement };
}

function download(blob: Blob, name: string) {
  const url = URL.createObjectURL(blob);
  const a = Object.assign(document.createElement("a"), { href: url, download: name });
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/** Paste a position, choose the view, and save the diagram as SVG or PNG. Everything happens in the browser. */
export function DiagramMaker() {
  const [input, setInput] = useState(START);
  const [flip, setFlip] = useState(false);
  const [coords, setCoords] = useState(true);
  const [marks, setMarks] = useState("");
  const [board, setBoard] = useState<BoardFinish>("walnut");
  const [size, setSize] = useState<(typeof SIZES)[number]>(800);
  const [copied, setCopied] = useState(false);

  // A position can arrive in the link (from the analysis board, or a shared diagram).
  useEffect(() => {
    const fen = new URLSearchParams(window.location.search).get("fen");
    if (fen) setInput(fen);
  }, []);

  const position = useMemo(() => readPosition(input), [input]);
  const highlight = useMemo(() => [...new Set(marks.toLowerCase().match(/\b[a-h][1-8]\b/g) ?? [])], [marks]);
  const svg = useMemo(() => (position.placement ? boardSvg(`${position.placement} w - - 0 1`, { flip, coords, highlight, board }) : ""), [position, flip, coords, highlight, board]);

  function savePng() {
    const image = new Image();
    const url = URL.createObjectURL(new Blob([svg], { type: "image/svg+xml" }));
    image.onload = () => {
      const canvas = Object.assign(document.createElement("canvas"), { width: size, height: size });
      canvas.getContext("2d")!.drawImage(image, 0, 0, size, size);
      URL.revokeObjectURL(url);
      canvas.toBlob((blob) => blob && download(blob, "chess-diagram.png"), "image/png");
    };
    image.src = url;
  }

  async function copyLink() {
    if (!position.placement) return;
    const link = `${window.location.origin}/tools/chess-diagram?fen=${encodeURIComponent(position.placement)}`;
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      window.prompt("Copy this link", link);
    }
  }

  return (
    <div className="grid gap-8 lg:grid-cols-[minmax(0,1fr)_minmax(0,460px)]">
      <div className="space-y-4">
        <div>
          <label htmlFor="diagram-input" className="text-sm font-semibold">
            FEN or PGN
          </label>
          <textarea
            id="diagram-input"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            rows={4}
            spellCheck={false}
            className="mt-1 w-full rounded-lg border border-border bg-surface px-3 py-2 font-mono text-sm"
          />
          {position.error ? (
            <p role="alert" className="mt-1 text-sm text-[var(--accent)]">
              {position.error}
            </p>
          ) : (
            <p className="mt-1 text-sm text-muted">Paste a FEN, a board layout such as 8/8/8/4k3/8/8/8/4K2R, or a PGN to draw its final position.</p>
          )}
        </div>
        <div>
          <label htmlFor="diagram-marks" className="text-sm font-semibold">
            Highlight squares
          </label>
          <input
            id="diagram-marks"
            value={marks}
            onChange={(e) => setMarks(e.target.value)}
            placeholder="e.g. e4 d5"
            className="mt-1 w-full rounded-lg border border-border bg-surface px-3 py-2 font-mono text-sm"
          />
        </div>
        <fieldset className="flex flex-wrap items-center gap-x-6 gap-y-3 text-sm">
          <legend className="sr-only">View options</legend>
          <label className="flex items-center gap-2">
            <input type="checkbox" checked={flip} onChange={(e) => setFlip(e.target.checked)} /> Black at the bottom
          </label>
          <label className="flex items-center gap-2">
            <input type="checkbox" checked={coords} onChange={(e) => setCoords(e.target.checked)} /> Coordinates
          </label>
          <label className="flex items-center gap-2">
            Board
            <select value={board} onChange={(e) => setBoard(e.target.value as BoardFinish)} className="rounded border border-border bg-surface px-2 py-1">
              {(Object.keys(BOARDS) as BoardFinish[]).map((b) => (
                <option key={b} value={b}>
                  {BOARDS[b].name}
                </option>
              ))}
            </select>
          </label>
          <label className="flex items-center gap-2">
            PNG size
            <select value={size} onChange={(e) => setSize(Number(e.target.value) as (typeof SIZES)[number])} className="rounded border border-border bg-surface px-2 py-1">
              {SIZES.map((s) => (
                <option key={s} value={s}>
                  {s} px
                </option>
              ))}
            </select>
          </label>
        </fieldset>
        <div className="flex flex-wrap gap-2">
          <button type="button" onClick={savePng} disabled={!svg} className="rounded-lg bg-[var(--accent)] px-4 py-2 text-sm font-medium text-[var(--accent-ink)] disabled:opacity-50">
            Download PNG
          </button>
          <button type="button" onClick={() => download(new Blob([svg], { type: "image/svg+xml" }), "chess-diagram.svg")} disabled={!svg} className="rounded-lg border border-border px-4 py-2 text-sm disabled:opacity-50">
            Download SVG
          </button>
          <button type="button" onClick={copyLink} disabled={!svg} className="rounded-lg border border-border px-4 py-2 text-sm disabled:opacity-50">
            {copied ? "Link copied" : "Copy link to this diagram"}
          </button>
        </div>
      </div>
      <div>
        {svg ? (
          <div role="img" aria-label="Diagram preview" className="overflow-hidden rounded-lg border border-border [&>svg]:block [&>svg]:h-auto [&>svg]:w-full" dangerouslySetInnerHTML={{ __html: svg }} />
        ) : (
          <div className="grid aspect-square place-items-center rounded-lg border border-dashed border-border text-sm text-muted">The diagram appears here.</div>
        )}
      </div>
    </div>
  );
}
